import { useChatsData } from "@/data/use-chats-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faTurnRight,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";
import { useExpertSelection } from "../Expert/selection/ExpertSelectionContext";
import { ProjectExpertsIndicator } from "../Expert/selection/ProjectExpertsIndicator";
import { DeleteDialog } from "../dialogs/DeleteDialog";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useAssistContext } from "./AssistContext";

type AssistChatMenuProps = {
  onNewChat?: () => void;
  onDeleteChat?: () => void;
};

export const AssistChatMenu: React.FC<AssistChatMenuProps> = ({
  onNewChat,
  onDeleteChat,
}) => {
  const {
    activeChatUuid,
    activeChat,
    setActiveChatUuid,
    forwardChatUuid,
    setForwardChatUuid,
  } = useAssistContext();
  const { selectExperts, kitType } = useExpertSelection();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { deleteChat } = useChatsData(groupUuid, kitUuid);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const selectCurrentChatExperts = () => {
    const experts = activeChat?.expertUuid
      ? [activeChat.expertUuid]
      : (activeChat?.summarizedChats.map((chat) => chat.expertUuid) ?? []);
    if (experts.length > 0) {
      selectExperts(new Set(experts));
    }
  };

  const handleNewChat = () => {
    selectCurrentChatExperts();
    setActiveChatUuid(null);
    setForwardChatUuid(null);
    if (onNewChat) onNewChat();
  };

  const handleForwardChat = () => {
    selectCurrentChatExperts();
    setForwardChatUuid(activeChatUuid);
    setActiveChatUuid(null);
    if (onNewChat) onNewChat();
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteChat = () => {
    if (!activeChatUuid) return;
    deleteChat(activeChatUuid);
    setActiveChatUuid(null);
    setForwardChatUuid(null);
    if (onDeleteChat) onDeleteChat();
  };

  return (
    <>
      {(activeChatUuid || forwardChatUuid) && (
        <Tooltip>
          <TooltipContent>New chat</TooltipContent>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNewChat}
              aria-label="New chat"
            >
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          </TooltipTrigger>
        </Tooltip>
      )}
      {activeChatUuid && (
        <>
          <Tooltip>
            <TooltipContent>Continue chat in a new thread</TooltipContent>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleForwardChat}
                aria-label="Continue chat in a new thread"
              >
                <FontAwesomeIcon icon={faTurnRight} />
              </Button>
            </TooltipTrigger>
          </Tooltip>
          <Tooltip>
            <TooltipContent>Delete chat</TooltipContent>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive"
                onClick={handleDeleteClick}
                aria-label="Delete chat"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </TooltipTrigger>
          </Tooltip>
        </>
      )}

      <DeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => setIsDeleteDialogOpen(open)}
        onDeleteClick={handleDeleteChat}
        name="Chat"
      />

      {kitType === "project" && (
        <>
          <ProjectExpertsIndicator />
        </>
      )}
    </>
  );
};
